import has from 'lodash/has';
import memoize from 'lodash/memoize';

type StyleArr = (string | Record<string, unknown>)[] | StyleArr[];

function jeCSShelper(base: $TSFixMe, arr: StyleArr): string[] {
  let classes: string[] = [];
  for (let i = 0; i < arr.length; i++) {
    const arg = arr[i];
    if (!arg) continue;
    const argType = typeof arg;
    if (argType === 'string' || argType === 'number') {
      classes.push(`${base}--${arg}`);
    } else if (Array.isArray(arg)) {
      classes = classes.concat(jeCSShelper(base, arg));
    } else if (typeof arg === 'object') {
      for (const key in arg) {
        if (has(arg, key) && arg[key]) {
          classes.push(`${base}--${key}`);
        }
      }
    }
  }
  return classes;
}

/**
 * A Jesse Dawson solution to pass CSS styles from a parent component to a child component.
 *
 * @param  {string} componentName - the child component's name-spaced name.
 * @param  {Array} styleArr - potential modifier class strings
 *    e.g. [...this.props.styleNames, { active: this.state.isActive }]
 *    recurses into arrays, [...classArr, classDict] == [classArr, classDict] == [[classArr], [[classDict]]]
 *    leaf level can be primitive (arr element) or key/value (dict element)
 *      if primitive: always include namespaced version in output
 *      if key/value: only include if value is truthy
 * @param  {object} [configurationObject]
 *    {Boolean} excludeBaseClass - if true, leave the base 'componentName' out of the return value
 * @returns {string} properly name-spaced class names for the child.
 *
 * example:
 *   call: jeCSS('testComponent', ['one', 'two', { a: true, b: false, c: 2 > 1 }], { excludeBaseClass: true })
 *   result: 'testComponent--one testComponent--two testComponent--a testComponent--c'
 */

export function jeCSS(
  componentName: string,
  styleArr: StyleArr,
  configurationObject: $TSFixMe,
): string {
  let styleArr_ = styleArr;
  if (!styleArr || styleArr.constructor !== Array) {
    styleArr_ = [styleArr];
  }
  const generatedClassNames = jeCSShelper(componentName, styleArr_);
  if (!configurationObject || !configurationObject.excludeBaseClass) {
    generatedClassNames.unshift(componentName);
  }
  return generatedClassNames.join(' ');
}

/* styale is a helper to create identical styleName arrays to prevent unnecessary rerenders
  ['a', 'b', 'c'] !== ['a', 'b', 'c']

  styale(['a', 'b', 'c']) === styale(['a', 'b', 'c'])

  styale(['a', 'b', 'c']) === styale(['c', 'b', 'a'])

  styale({ 'a': true, 'b': false, 'c': true }) === styale(['a', 'c'])
*/
const emptyArr: string[] = [];
const splitChar = ',';
const objConstructor = {}.constructor;
const styaleHelper = memoize(styleNameString => {
  return styleNameString.split(splitChar);
});

export function styale(styles: { [key: string]: $TSFixMe }): string[] {
  let styleArr = styles;
  if (styles.constructor === objConstructor) {
    styleArr = [];
    for (const style in styles) {
      if (has(styles, style) && styles[style]) {
        styleArr.push(style);
      }
    }
  }
  if (!styleArr || !styleArr.length) return emptyArr;
  return styaleHelper(styleArr.sort().join(splitChar));
}

/**
 *   This simulates an opacity fade as such, mostly interpolating the opacity
 *   between index 2 through n - 1:
 *   0: 1
 *   1: 0.5
 *   for i in n - 1: 0.5 - step(i)
 *   n: 0.1
 */
const STATION_LIST_OPACITY_STEP_ONE = 1;
const STATION_LIST_OPACITY_STEP_TWO = 0.5;
const STATION_LIST_OPACITY_LAST_STEP = 0.1;
const STATION_LIST_OPACITY_FIFTH_STEP = 0;

export function getStationListItemOpacity(
  index: number,
  count: number,
  isHovered: boolean,
  isShuffleEnabled: boolean,
): number {
  if (index >= count) {
    return 0;
  }
  if (isHovered) {
    return STATION_LIST_OPACITY_STEP_ONE;
  }
  if (index === 0) {
    return STATION_LIST_OPACITY_STEP_ONE;
  }
  if (index === count - 1 && count > 2) {
    if (isShuffleEnabled) {
      return STATION_LIST_OPACITY_FIFTH_STEP;
    }
    return STATION_LIST_OPACITY_LAST_STEP;
  }
  if (index === 1) {
    return STATION_LIST_OPACITY_STEP_TWO;
  }
  const step = (STATION_LIST_OPACITY_STEP_TWO - STATION_LIST_OPACITY_LAST_STEP) / (count - 2);
  return STATION_LIST_OPACITY_STEP_TWO - step * (index - 1);
}
